import { useState } from 'react'
import { ArrowUpDown, CheckCircle, XCircle, TrendingUp, ArrowUp } from 'lucide-react'
import type { AiqQuestionComparison } from '../../types/aiq-report'

const DOMAIN_LABELS: Record<string, string> = {
  abstraction: '무시',
  change: '변화',
  relation: '관계',
}

type SortField = 'difficulty' | 'domain' | 'improved'
type SortDir = 'asc' | 'desc'

interface Props {
  questions: AiqQuestionComparison[]
  hasPostAiq?: boolean
}

export function QuestionBreakdownTable({ questions, hasPostAiq = true }: Props) {
  const [sortField, setSortField] = useState<SortField>('difficulty')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const hasAdvanced = questions.some(q => q.preScoreAdvanced != null)

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  const sorted = [...questions].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    switch (sortField) {
      case 'difficulty': return (a.difficultyLevel - b.difficultyLevel) * dir
      case 'domain': return ((a.domains[0] || '') > (b.domains[0] || '') ? 1 : -1) * dir
      case 'improved': return ((a.improved ? 1 : 0) - (b.improved ? 1 : 0)) * -dir
      default: return 0
    }
  })

  const formatTime = (sec: number | null) => {
    if (sec == null) return '-'
    if (sec < 60) return `${sec}초`
    return `${Math.floor(sec / 60)}분 ${sec % 60}초`
  }

  const formatScore = (score: number | null) => {
    if (score == null) return '-'
    return Math.round(score).toString()
  }

  const ResultIcon = ({ correct }: { correct: boolean }) =>
    correct
      ? <CheckCircle className="w-5 h-5 text-green-500" />
      : <XCircle className="w-5 h-5 text-red-400" />

  return (
    <div className="bg-white rounded-xl shadow-2xs border border-gray-100 overflow-hidden">
      <div className="p-6 pb-4">
        <h3 className="text-lg font-semibold text-gray-900">문제별 상세 비교</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-y border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => toggleSort('domain')}>
                <span className="flex items-center gap-1">분야 <ArrowUpDown className="w-3 h-3" /></span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => toggleSort('difficulty')}>
                <span className="flex items-center gap-1">난이도 <ArrowUpDown className="w-3 h-3" /></span>
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pre 결과</th>
              {hasPostAiq && (
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Post 결과</th>
              )}
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pre 시간</th>
              {hasPostAiq && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Post 시간</th>
              )}
              {hasAdvanced && (
                <>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pre 점수</th>
                  {hasPostAiq && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Post 점수</th>
                  )}
                </>
              )}
              {hasPostAiq && (
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => toggleSort('improved')}>
                  <span className="flex items-center justify-center gap-1">향상 <ArrowUpDown className="w-3 h-3" /></span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((q, i) => (
              <tr key={q.quizId} className={`hover:bg-gray-50 ${q.improved ? 'bg-green-50/30' : ''}`}>
                <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {q.domains.length > 0 ? q.domains.map(d => (
                      <span key={d} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                        {DOMAIN_LABELS[d] || d}
                      </span>
                    )) : <span className="text-gray-400">-</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }, (_, j) => (
                      <div key={j} className={`w-2 h-2 rounded-full ${j < q.difficultyLevel ? 'bg-amber-400' : 'bg-gray-200'}`} />
                    ))}
                    <span className="ml-1 text-xs text-gray-500">Lv.{q.difficultyLevel}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center"><ResultIcon correct={q.preCorrect} /></td>
                {hasPostAiq && (
                  <td className="px-4 py-3 text-center"><ResultIcon correct={q.postCorrect} /></td>
                )}
                <td className="px-4 py-3 text-right text-gray-600">{formatTime(q.preElapsedSec)}</td>
                {hasPostAiq && (
                  <td className="px-4 py-3 text-right text-gray-600">{formatTime(q.postElapsedSec)}</td>
                )}
                {hasAdvanced && (
                  <>
                    <td className="px-4 py-3 text-right text-gray-600 font-mono text-xs">
                      {formatScore(q.preScoreAdvanced)}
                    </td>
                    {hasPostAiq && (
                      <td className="px-4 py-3 text-right text-gray-600 font-mono text-xs">
                        {formatScore(q.postScoreAdvanced)}
                      </td>
                    )}
                  </>
                )}
                {hasPostAiq && (
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      {/* 큰 화살표: 오답→정답 (binary 향상) */}
                      {q.improved && <TrendingUp className="w-5 h-5 text-green-500" />}
                      {/* 작은 화살표: advanced score 향상 (binary 동일해도) */}
                      {!q.improved && q.advancedImproved && <ArrowUp className="w-4 h-4 text-blue-400" />}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
